import React, { useState, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import toast, { showToast } from '../utils/toast';
import { Helmet } from 'react-helmet-async';
import './LoginPage.css';
import './SocialLinks.css';
import logo from '../assets/pokeshani-logo.png';

const LoginPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({
    email: '',
    password: ''
  });

  const validateForm = () => {
    let isValid = true;
    const newErrors = {
      email: '',
      password: ''
    };

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
      isValid = false;
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const navigate = useNavigate();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      const loadingToast = showToast.loading('Signing in...');
      try {
        const { data, error } = await supabase.auth.signInWithPassword({
          email: formData.email,
          password: formData.password
        });

        if (error) {
          toast.dismiss(loadingToast);
          showToast.error(error.message);
          setErrors(prev => ({
            ...prev,
            password: error.message
          }));
          return;
        }

        if (data.user) {
          toast.dismiss(loadingToast);
          showToast.success('Welcome back!');
          navigate('/dashboard');
        }
      } catch (error) {
        toast.dismiss(loadingToast);
        showToast.error('An unexpected error occurred');
        console.error('Error during login:', error);
        setErrors(prev => ({
          ...prev,
          password: 'An unexpected error occurred'
        }));
      }
    }
  };

  const handleGoogleLogin = async () => {
    const loadingToast = showToast.loading('Signing in with Google...');
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/dashboard`
        }
      });

      if (error) {
        toast.dismiss(loadingToast);
        showToast.error(error.message);
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      showToast.error('An unexpected error occurred during Google sign-in');
      console.error('Error during Google login:', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  return (
    <div className="landing-container">
      <div className="social-links">
        <a
          href="https://x.com/PokeShani"
          target="_blank"
          rel="noopener noreferrer"
          className="social-link"
          aria-label="Twitter"
        >
          <svg viewBox="0 0 24 24">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
          </svg>
        </a>
        <a
          href="https://www.instagram.com/pokeshaniofficial?igsh=NHF3aHd4aXY5Y3Ux"
          target="_blank"
          rel="noopener noreferrer"
          className="social-link instagram"
          aria-label="Instagram"
        >
          <svg viewBox="0 0 24 24">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.072 4.849-.072zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
          </svg>
        </a>
      </div>
      <Helmet>
        <title>PokeShani - Sell Digital Products with 0% Platform Fees | Instant PayPal Payouts</title>
        <meta name="description" content="Create and sell digital products on PokeShani - the creator-first platform with zero fees. Instant PayPal payouts, courses, ebooks, and more. Start your digital store today!" />
        <meta name="keywords" content="digital products, online courses, ebooks, zero platform fees, instant payouts, PayPal payments, creator platform, digital marketplace" />
        <meta property="og:title" content="PokeShani - Sell Digital Products with No Platform Fees" />
        <meta property="og:description" content="Launch your digital product store with 0% platform fees. Instant PayPal payouts for courses, ebooks, and digital downloads." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="PokeShani - Creator-First Digital Product Platform" />
        <meta name="twitter:description" content="Sell digital products with no platform fees. Instant PayPal payouts, complete control over your earnings." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "PokeShani",
            "description": "Platform for creators to sell digital products with no platform fees and instant PayPal payouts",
            "url": "https://pokeshani.com",
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://pokeshani.com/search?q={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          })}
        </script>
      </Helmet>
      <div className="help-link">
        <Link to="/faq" className="help-button">Help</Link>
      </div>
      <div className="auth-section">
        <div className="auth-content">
          <div className="welcome-section">
            <img src={logo} alt="PokeShani Logo" className="logo" />
            <h1>Welcome Back to PokeShani</h1>
            <p>Don't have an account? <Link to="/signup" className="signup-link">Sign up</Link></p>
          </div>
          
          {/* Google Login Button - Moved to top */}
          <button 
            type="button" 
            className="google-login-btn"
            onClick={handleGoogleLogin}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            <span>Continue with Google</span>
          </button>
          
          {/* Divider */}
          <div className="divider">
            <span>or</span>
          </div>
          
          <form className="login-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <input
                type="email"
                name="email"
                placeholder="Enter email address"
                value={formData.email}
                onChange={handleChange}
                required
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>
            <div className="form-group password-input">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="Enter password"
                value={formData.password}
                onChange={handleChange}
                required
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
              {errors.password && <span className="error-message">{errors.password}</span>}
            </div>
            <button type="submit" className="login-btn">Login</button>
          </form>
        </div>
      </div>
      <div className="showcase-section">
        <div className="showcase-content">
          <h2>Welcome to PokeShani</h2>
          <p>Your Ultimate Platform for Digital Product Sales</p>
          <div className="feature-grid">
            <div className="feature-item">
              <h3>Easy to Start</h3>
              <p>Create your digital product listings in minutes with our intuitive interface</p>
            </div>
            <div className="feature-item">
              <h3>Secure Payments</h3>
              <p>Integrated PayPal payments ensure safe and reliable transactions</p>
            </div>
            <div className="feature-item">
              <h3>Real-time Analytics</h3>
              <p>Track your sales, revenue, and customer insights with detailed dashboards</p>
            </div>
            <div className="feature-item">
              <h3>Keep 100% Earnings</h3>
              <p>No platform fees - keep all your earnings with instant payouts</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;